import { useState, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Plane, MapPin, Calendar, ArrowRight, TrendingDown, Clock, Route } from 'lucide-react';
import { buscaPassagensWidget } from '@/lib/widgets';
import { useTranslation } from '@/hooks/useTranslation';

/* ═══════════════════════════════════════════
   BUSCA DE PASSAGENS — Widget Vai de Promo v2
   Design premium com rota visual, chips de
   origens populares e preço estimado
   ═══════════════════════════════════════════ */

interface Aeroporto {
  codigo: string;
  cidade: string;
  estado: string;
  pais: string;
  precoMedio: string;
  popular?: boolean;
}

const AEROPORTOS: Aeroporto[] = [
  { codigo: 'GRU', cidade: 'São Paulo', estado: 'GRU', pais: 'Brasil', precoMedio: 'R$ 250–450', popular: true },
  { codigo: 'CGH', cidade: 'São Paulo', estado: 'CGH', pais: 'Brasil', precoMedio: 'R$ 280–500', popular: true },
  { codigo: 'GIG', cidade: 'Rio de Janeiro', estado: 'GIG', pais: 'Brasil', precoMedio: 'R$ 300–550', popular: true },
  { codigo: 'SDU', cidade: 'Rio de Janeiro', estado: 'SDU', pais: 'Brasil', precoMedio: 'R$ 320–580', popular: true },
  { codigo: 'BSB', cidade: 'Brasília', estado: 'BSB', pais: 'Brasil', precoMedio: 'R$ 350–600', popular: true },
  { codigo: 'CNF', cidade: 'Belo Horizonte', estado: 'CNF', pais: 'Brasil', precoMedio: 'R$ 280–500' },
  { codigo: 'POA', cidade: 'Porto Alegre', estado: 'POA', pais: 'Brasil', precoMedio: 'R$ 200–400', popular: true },
  { codigo: 'FLN', cidade: 'Florianópolis', estado: 'FLN', pais: 'Brasil', precoMedio: 'R$ 180–350' },
  { codigo: 'VCP', cidade: 'Campinas', estado: 'VCP', pais: 'Brasil', precoMedio: 'R$ 220–400' },
  { codigo: 'SSA', cidade: 'Salvador', estado: 'SSA', pais: 'Brasil', precoMedio: 'R$ 450–750' },
  { codigo: 'REC', cidade: 'Recife', estado: 'REC', pais: 'Brasil', precoMedio: 'R$ 500–850' },
  { codigo: 'FOR', cidade: 'Fortaleza', estado: 'FOR', pais: 'Brasil', precoMedio: 'R$ 480–800' },
  { codigo: 'MAO', cidade: 'Manaus', estado: 'MAO', pais: 'Brasil', precoMedio: 'R$ 600–1.000' },
];

const POPULARES = AEROPORTOS.filter(a => a.popular);

function formatarDataAAMMDD(dateStr: string): string {
  if (!dateStr) return '';
  const [ano, mes, dia] = dateStr.split('-');
  return `${ano?.slice(-2) || ''}${mes}${dia}`;
}

function gerarLink(origem: string, dataIda: string, dataVolta: string): string {
  const ida = formatarDataAAMMDD(dataIda);
  const volta = formatarDataAAMMDD(dataVolta);
  const base = 'https://www.vaidepromo.com.br/passagens-aereas/pesquisa';
  const trecho = `${origem}CWB${ida}-CWB${origem}${volta}`;
  return `${base}/${trecho}/1/0/0/Y/?utm_medium=afiliado&pcrid=14139&utm_source=site-blog&pcrtt=widget:home`;
}

function dataMinima(): string {
  const hoje = new Date();
  hoje.setDate(hoje.getDate() + 7);
  return hoje.toISOString().split('T')[0];
}

function dataVoltaPadrao(): string {
  const hoje = new Date();
  hoje.setDate(hoje.getDate() + 10);
  return hoje.toISOString().split('T')[0];
}

function diaSeguinte(dateStr: string): string {
  const [ano, mes, dia] = dateStr.split('-').map(Number);
  const data = new Date(ano, mes - 1, dia);
  data.setDate(data.getDate() + 1);
  return data.toISOString().split('T')[0];
}

function formatarDataCurta(dateStr: string): string {
  if (!dateStr) return '';
  const [ano, mes, dia] = dateStr.split('-');
  return `${dia}/${mes}`;
}

function diasEntre(ida: string, volta: string): number {
  const d1 = new Date(ida);
  const d2 = new Date(volta);
  return Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));
}

export function BuscaPassagensWidget() {
  const { t } = useTranslation();
  const [origem, setOrigem] = useState('GRU');
  const [dataIda, setDataIda] = useState(dataMinima());
  const [dataVolta, setDataVolta] = useState(dataVoltaPadrao());
  const [mostrarTodasOrigens, setMostrarTodasOrigens] = useState(false);

  const aeroportoSelecionado = useMemo(
    () => AEROPORTOS.find(a => a.codigo === origem) || AEROPORTOS[0],
    [origem]
  );

  const handleMudarIda = useCallback(
    (novaIda: string) => {
      setDataIda(novaIda);
      const minVolta = diaSeguinte(novaIda);
      if (dataVolta <= novaIda) {
        setDataVolta(minVolta);
      }
    },
    [dataVolta]
  );

  const handleBuscar = useCallback(() => {
    if (!dataIda || !dataVolta) return;
    const url = gerarLink(origem, dataIda, dataVolta);
    buscaPassagensWidget.trackClick({
      origem,
      destino: 'CWB',
      data_ida: dataIda,
      data_volta: dataVolta,
      dias_estadia: diasEntre(dataIda, dataVolta),
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [origem, dataIda, dataVolta]);

  const schema = buscaPassagensWidget.getSchema('https://www.vaidepromo.com.br/passagens-aereas/pesquisa');
  const duracaoViagem = diasEntre(dataIda, dataVolta);

  const origensVisiveis = mostrarTodasOrigens ? AEROPORTOS : POPULARES;

  return (
    <div
      className="bg-gradient-to-br from-ocean-50 via-white to-sand-50 rounded-2xl border border-sand-200 shadow-[0_4px_16px_rgba(0,0,0,0.06)] overflow-hidden"
      {...buscaPassagensWidget.getDataAttrs()}
    >
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({ '@context': 'https://schema.org', ...schema })}
        </script>
      </Helmet>

      {/* Header com gradiente */}
      <div className="bg-gradient-to-r from-ocean to-teal-deep px-6 py-5 text-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Plane className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-display text-lg tracking-wide">{t('widget.flights.title')}</h4>
            <p className="font-body text-xs text-teal-100">{t('widget.flights.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* Rota visual */}
        <div className="bg-sand-50 rounded-xl p-4 border border-sand-100">
          <div className="flex items-center justify-between">
            <div className="text-center flex-1">
              <div className="w-8 h-8 rounded-full bg-ocean/10 text-ocean flex items-center justify-center mx-auto mb-1">
                <MapPin className="w-4 h-4" />
              </div>
              <p className="font-body text-[10px] uppercase tracking-wider text-warm-gray">{aeroportoSelecionado.cidade}</p>
              <p className="font-display text-sm text-warm-dark">{aeroportoSelecionado.codigo}</p>
            </div>

            <div className="flex flex-col items-center px-2">
              <div className="flex items-center gap-1 text-ocean">
                <ArrowRight className="w-4 h-4" />
                <Plane className="w-3 h-3" />
                <ArrowRight className="w-4 h-4" />
              </div>
              <span className="font-body text-[10px] text-warm-gray mt-0.5">1h–1h30</span>
            </div>

            <div className="text-center flex-1">
              <div className="w-8 h-8 rounded-full bg-teal-50 text-teal-deep flex items-center justify-center mx-auto mb-1">
                <MapPin className="w-4 h-4" />
              </div>
              <p className="font-body text-[10px] uppercase tracking-wider text-warm-gray">Curitiba</p>
              <p className="font-display text-sm text-warm-dark">CWB</p>
            </div>
          </div>

          {/* Continuação da rota */}
          <div className="mt-3 pt-3 border-t border-sand-200 flex items-center justify-center gap-2 text-warm-gray flex-wrap">
            <Route className="w-3 h-3 flex-shrink-0" />
            <span className="font-body text-[11px] text-center">
              + 1h15 de carro até Pontal do Sul → 30 min de balsa → Ilha do Mel
            </span>
          </div>
        </div>

        {/* Chips de origens populares */}
        <div>
          <p className="font-body text-xs font-semibold text-warm-dark mb-2 flex items-center gap-1">
            <TrendingDown className="w-3 h-3 text-coral" />
            Origens mais buscadas
          </p>
          <div className="flex flex-wrap gap-2">
            {origensVisiveis.map(a => (
              <button
                key={a.codigo}
                onClick={() => setOrigem(a.codigo)}
                className={`px-3 py-1.5 rounded-lg text-xs font-body font-medium transition-all ${
                  origem === a.codigo
                    ? 'bg-ocean text-white shadow-md'
                    : 'bg-white border border-sand-200 text-warm-gray hover:border-ocean hover:text-ocean'
                }`}
              >
                {a.cidade} ({a.codigo})
                {a.popular && <span className="ml-1 text-[9px] opacity-70">★</span>}
              </button>
            ))}
          </div>
          {!mostrarTodasOrigens && AEROPORTOS.length > POPULARES.length && (
            <button
              onClick={() => setMostrarTodasOrigens(true)}
              className="mt-2 text-xs text-ocean font-body hover:underline"
            >
              Ver todas as {AEROPORTOS.length} origens
            </button>
          )}
        </div>

        {/* Preço estimado */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <TrendingDown className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="font-body text-xs text-warm-gray">Preço médio ida e volta</p>
            <p className="font-display text-lg text-amber-700">{aeroportoSelecionado.precoMedio}</p>
          </div>
        </div>

        {/* Datas */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="data-ida" className="flex items-center gap-1 font-body text-xs font-semibold text-warm-dark mb-1.5">
              <Calendar className="w-3 h-3 text-ocean" />
              {t('widget.flights.departureLabel')}
            </label>
            <input
              id="data-ida"
              type="date"
              value={dataIda}
              min={dataMinima()}
              onChange={e => handleMudarIda(e.target.value)}
              className="w-full bg-white border border-sand-200 rounded-xl px-4 py-3 font-body text-sm text-warm-dark focus:outline-none focus:border-ocean focus:ring-2 focus:ring-ocean/10 transition-all"
            />
          </div>
          <div>
            <label htmlFor="data-volta" className="flex items-center gap-1 font-body text-xs font-semibold text-warm-dark mb-1.5">
              <Calendar className="w-3 h-3 text-ocean" />
              {t('widget.flights.returnLabel')}
            </label>
            <input
              id="data-volta"
              type="date"
              value={dataVolta}
              min={diaSeguinte(dataIda)}
              onChange={e => setDataVolta(e.target.value)}
              className="w-full bg-white border border-sand-200 rounded-xl px-4 py-3 font-body text-sm text-warm-dark focus:outline-none focus:border-ocean focus:ring-2 focus:ring-ocean/10 transition-all"
            />
          </div>
        </div>

        {/* Resumo da viagem */}
        <div className="flex items-center gap-3 text-xs font-body text-warm-gray bg-sand-50 rounded-lg px-3 py-2">
          <Clock className="w-3 h-3" />
          <span>
            {formatarDataCurta(dataIda)} → {formatarDataCurta(dataVolta)} · {duracaoViagem} {duracaoViagem === 1 ? 'dia' : 'dias'} na ilha
          </span>
        </div>

        {/* CTA */}
        <button
          onClick={handleBuscar}
          className="w-full bg-gradient-to-r from-coral to-[#d4715a] hover:from-[#c96a52] hover:to-[#b85f49] text-white font-body font-bold rounded-xl px-6 py-4 transition-all uppercase tracking-wide flex items-center justify-center gap-2 shadow-lg shadow-coral/20 hover:shadow-xl hover:shadow-coral/30 hover:-translate-y-0.5"
        >
          <Plane className="w-5 h-5" />
          <span>{t('widget.flights.searchButton')}</span>
          <ArrowRight className="w-4 h-4" />
        </button>

        {/* Disclaimer */}
        <p className="font-body text-[11px] text-warm-gray text-center leading-relaxed">
          {t('widget.flights.disclaimer')}
        </p>
      </div>
    </div>
  );
}
